import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { watchDebounced } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy } from "@utils/toolBox";
import handleError from "@utils/composables/handleError";
import { captureEvent } from "@/lib/analytics";

export const useMCampaignTargetsStore = defineStore("mCampaignTargets", () => {
  const campaignTargets = ref([]);
  const initialCampaignTargets = ref([]);
  const currentMCampaignToken = ref(null);
  const searchQuery = ref("");
  const statusFilter = ref([]);

  const hasTargetsChanged = computed(
    () => JSON.stringify(initialCampaignTargets?.value) !== JSON.stringify(campaignTargets?.value),
  );

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (searchQuery.value.trim()) params.set("query", searchQuery.value.trim());
    statusFilter.value.forEach((s) => params.append("status[]", s));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  const fetchMCampaignTargets = async (mCampaignToken) => {
    currentMCampaignToken.value = mCampaignToken;
    try {
      const { response, data } = await useMyFetch(
        `/m_campaigns/${mCampaignToken}/campaign_targets${buildQuery()}`,
      ).json();
      if (response.value.ok) {
        campaignTargets.value = data.value;
        initialCampaignTargets.value = deepCopy(data.value);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const search = async () => {
    if (!currentMCampaignToken.value) return;
    await fetchMCampaignTargets(currentMCampaignToken.value);
  };

  const clearFilters = async () => {
    searchQuery.value = "";
    statusFilter.value = [];
    await search();
  };

  const saveMCampaignTargets = async () => {
    const saved = await Promise.all(
      campaignTargets.value.map(async (target, idx) => {
        const { data, response } = await useMyFetch(`/campaign_targets/${target.token}`).patch({ target }).json();
        campaignTargets.value[idx] = data.value;
        initialCampaignTargets.value[idx] = deepCopy(data.value);
        return response.value.ok;
      }),
    );

    if (saved.every(Boolean)) captureEvent("m_campaign_targets_saved", { targets_count: saved.length });
  };

  watchDebounced(searchQuery, () => search(), { debounce: 300 });

  return {
    hasTargetsChanged,
    campaignTargets,
    initialCampaignTargets,
    searchQuery,
    statusFilter,
    fetchMCampaignTargets,
    saveMCampaignTargets,
    search,
    clearFilters,
  };
});
